import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { CellProps } from 'react-datasheet-grid'
import type { DeviceSpecTag } from '../../types'
import type { FormulaContext } from './formula'
import { suggest } from './formulaSuggest'
import type { SuggestState } from './formulaSuggest'
import { cellSource, setCellInput } from './recompute'
import type { FormulaField } from './recompute'

// 태그 표의 글자 칸 + '=' 수식 자동완성. react-datasheet-grid 의 textColumn 과 같은 규칙으로 동작한다:
// 편집을 시작하면 글자 전체 선택(치면 덮어씀) · 편집이 끝날 때 한 번 값 확정 · Esc 면 확정하지 않음.
// 엑셀처럼 칸에는 계산된 값이 보이고, 편집(F2·Enter·더블클릭)을 시작하면 수식 원문이 나온다 (recompute).
// '=' 로 시작하면 커서 앞 글자에 맞춰 목록을 띄우고 ↑↓ 로 고르고 Enter/Tab 으로 넣는다 (Esc 로 닫기).

export interface FormulaCellData {
  field: FormulaField
  parse: (value: string) => string
  device: FormulaContext['device'] // 자동완성 목록에 지금 값 미리보기
  // 수식이 아닐 때의 자동완성 (FANUC 주소 등)
  addressSuggest?: (value: string, caret: number) => SuggestState | null
}

type Popup = SuggestState & { index: number; left: number; top: number }

export default function FormulaCell({ focus, rowData, setRowData, columnData }: CellProps<DeviceSpecTag, FormulaCellData>) {
  const shown = rowData[columnData.field] ?? ''
  const ref = useRef<HTMLInputElement>(null)
  const latest = useRef({ rowData, setRowData, columnData })
  const session = useRef({ focusedAt: 0, changedAt: 0, escPressed: false, mounted: false })
  const [popup, setPopup] = useState<Popup | null>(null)

  useLayoutEffect(() => { latest.current = { rowData, setRowData, columnData } })

  // 편집 시작·끝 (textColumn 과 같음)
  useLayoutEffect(() => {
    const el = ref.current
    const s = session.current
    if (!el) return
    if (focus) {
      el.value = cellSource(latest.current.rowData, latest.current.columnData.field)
      el.focus()
      el.select()
      s.escPressed = false
      s.focusedAt = Date.now()
    } else {
      if (s.mounted && !s.escPressed && s.changedAt >= s.focusedAt) {
        const { rowData: row, columnData: c } = latest.current
        latest.current.setRowData(setCellInput(row, c.field, el.value, c.parse))
      }
      el.blur()
    }
    s.mounted = true
  }, [focus])

  // 편집 중이 아닐 때는 값을 보여 준다
  useEffect(() => {
    if (!focus && ref.current) ref.current.value = shown
  }, [focus, shown])

  const refresh = () => {
    const el = ref.current
    if (!el || document.activeElement !== el) { setPopup(null); return }
    const caret = el.selectionStart ?? el.value.length
    const { columnData: c } = latest.current
    const state = suggest(el.value, caret, c.device) ?? (el.value.trimStart().startsWith('=') ? null : c.addressSuggest?.(el.value, caret) ?? null)
    if (!state) { setPopup(null); return }
    const box = el.getBoundingClientRect()
    setPopup(p => ({ ...state, index: p && p.from === state.from ? Math.min(p.index, state.items.length - 1) : 0, left: box.left, top: box.bottom + 2 }))
  }

  const apply = (p: Popup, i: number) => {
    const el = ref.current
    const item = p.items[i]
    if (!el || !item) return
    el.value = el.value.slice(0, p.from) + item.insert + el.value.slice(p.to)
    const caret = p.from + item.insert.length
    el.setSelectionRange(caret, caret)
    session.current.changedAt = Date.now()
    refresh()
  }

  // 목록이 떠 있는 동안 ↑↓ Enter Tab Esc 는 목록이 먼저 — 그리드(document)보다 앞(window capture)에서 가로챈다
  useEffect(() => {
    if (!popup) return
    const onKey = (e: KeyboardEvent) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key) || e.isComposing) return
      e.preventDefault()
      e.stopImmediatePropagation()
      if (e.key === 'Escape') setPopup(null)
      else if (e.key === 'ArrowDown') setPopup({ ...popup, index: (popup.index + 1) % popup.items.length })
      else if (e.key === 'ArrowUp') setPopup({ ...popup, index: (popup.index - 1 + popup.items.length) % popup.items.length })
      else apply(popup, popup.index)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  })

  return (
    <>
      <input ref={ref} defaultValue={shown} className="dsg-input" tabIndex={-1}
        style={{ pointerEvents: focus ? 'auto' : 'none' }}
        onChange={() => { session.current.changedAt = Date.now(); refresh() }}
        onKeyDown={e => { if (e.key === 'Escape') session.current.escPressed = true }}
        onKeyUp={e => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End') refresh() }}
        onClick={refresh}
        onFocus={refresh}
        onBlur={() => setPopup(null)} />
      {focus && popup && createPortal(
        <div className="formula-suggest fixed z-[60] min-w-[260px] max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-surface shadow-lg py-1 text-xs"
          style={{ left: popup.left, top: popup.top }}
          onMouseDown={e => e.preventDefault() /* 입력칸 포커스 유지 */}>
          {popup.title && <div className="px-3 pt-1 pb-1.5 mb-1 border-b border-gray-100 text-[11px] font-medium text-gray-500">{popup.title}</div>}
          {popup.items.map((item, i) => (
            <div key={item.label} onMouseEnter={() => setPopup({ ...popup, index: i })} onClick={() => apply(popup, i)}
              className={`flex items-center gap-3 px-3 py-1.5 cursor-pointer ${i === popup.index ? 'bg-blue-50' : ''}`}>
              <span className="font-mono text-gray-800">{item.label}</span>
              <span className="text-gray-400">{item.hint}</span>
              {item.preview && <span className="ml-auto font-mono text-blue-700">{item.preview}</span>}
            </div>
          ))}
          <div className="px-3 pt-1 mt-1 border-t border-gray-100 text-[11px] text-gray-400">↑↓ 고르기 · Enter/Tab 넣기 · Esc 닫기</div>
        </div>,
        document.body,
      )}
    </>
  )
}
